<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type SignalHealthFund } from '../api/client'
import { useAuth } from '../composables/useAuth'
import { useViewAs } from '../composables/useViewAs'
import { isAcknowledged } from './signalHealthHelpers'
import { todayLondonIso } from './fof/fofFormat'

// Superuser-only red alert for the signal-health tiles (spec 2026-10-23 §5.4):
// shown on every tab while any fund (gated funds included) is Red and not
// within its config-dated acknowledgement. Hidden while viewing as another
// user; fetch errors render nothing (console only).
const { isSuperUser } = useAuth()
const { target } = useViewAs()
const funds = ref<SignalHealthFund[]>([])
let timer: ReturnType<typeof setInterval> | null = null

async function poll() {
  try {
    // A payload without a funds array leaves the banner empty instead of throwing in redFunds.
    const res = await api.signalHealth()
    funds.value = Array.isArray(res?.funds) ? res.funds : []
  } catch (e) {
    console.warn('signal-health alert poll failed', e)
  }
}

onMounted(() => { poll(); timer = setInterval(poll, 600_000) })
onUnmounted(() => { if (timer) clearInterval(timer) })

const redFunds = computed(() => {
  if (!isSuperUser.value || target.value !== null) return []
  const today = todayLondonIso()
  return funds.value.filter(f => f.status === 'red' && !isAcknowledged(f, today))
})
</script>

<template>
  <div v-if="redFunds.length" class="sh-alert" role="alert" data-testid="signal-health-alert">
    <span class="sh-alert-icon">🚨</span>
    <div class="sh-alert-body">
      <div class="sh-alert-title">SIGNAL HEALTH ALERT</div>
      <div v-for="f in redFunds" :key="f.fundId" class="sh-alert-msg">{{ f.label }}: {{ f.reason }}</div>
      <a href="#risk" class="sh-alert-link">View in Risk</a>
    </div>
  </div>
</template>

<style scoped>
.sh-alert { display: flex; align-items: center; gap: 0.75rem; background: #b71c1c; color: #fff;
  border: 2px solid #f44336; border-radius: 6px; padding: 0.75rem 1rem; margin-bottom: 0.6rem; font-weight: 700; }
.sh-alert-icon { font-size: 1.5rem; line-height: 1; }
.sh-alert-body { display: flex; flex-direction: column; gap: 0.15rem; }
.sh-alert-title { font-size: 0.95rem; letter-spacing: 0.03em; }
.sh-alert-msg { font-size: 0.85rem; font-weight: 600; }
.sh-alert-link { color: #fff; text-decoration: underline; font-size: 0.8rem; }
</style>
